// utils/getRelatedOffers.ts

import { IMeetingPlacePageOfferItem } from '@/lib/fragments/meetingPlacePageOfferItem';
import { IMeetingPlacePageTenantEntertainmentItem } from '@/lib/fragments/meetingPlacePageTenantEntertainmentItem';
import { IMeetingPlacePageTenantFoodBevItem } from '@/lib/fragments/meetingPlacePageTenantFoodBevItem';
import { IMeetingPlacePageTenantShopItem } from '@/lib/fragments/meetingPlacePageTenantShopItem';

const MAX_RELATED_OFFERS = 4;

type TenantItem =
  | IMeetingPlacePageTenantShopItem
  | IMeetingPlacePageTenantFoodBevItem
  | IMeetingPlacePageTenantEntertainmentItem;

const getPrimaryCategory = (
  offer: IMeetingPlacePageOfferItem,
): string | undefined => offer.tenant?.primaryCategory?.name;

const getSecondaryCategories = (offer: IMeetingPlacePageOfferItem): string[] =>
  offer.tenant?.secondaryCategoriesCollection?.items
    ?.map((c: any) => c?.name)
    .filter(Boolean) ?? [];

export function getTenantRelatedOffers(
  currentTenant: TenantItem,
  allActiveOffers: IMeetingPlacePageOfferItem[],
): IMeetingPlacePageOfferItem[] {
  if (!allActiveOffers?.length || !currentTenant) return [];

  const result: IMeetingPlacePageOfferItem[] = [];
  const usedIds = new Set<string>([]);

  const tenantId = currentTenant?.sys?.id;
  const currentPrimaryCategory = currentTenant?.primaryCategory?.name;
  const currentSecondaryCategories = new Set<string>(
    currentTenant?.secondaryCategoriesCollection?.items
      ?.map((c: any) => c?.name)
      .filter(Boolean) ?? [],
  );

  const fill = (candidates: IMeetingPlacePageOfferItem[]) => {
    for (const offer of candidates) {
      if (result.length >= MAX_RELATED_OFFERS) break;
      if (!usedIds.has(offer.sys.id)) {
        result.push(offer);
        usedIds.add(offer.sys.id);
      }
    }
  };

  // ── Priority 1: Same tenant ───────────────────────────────────────────────
  fill(allActiveOffers.filter((o) => o.tenant?.sys?.id === tenantId));
  if (result.length >= MAX_RELATED_OFFERS) return result;

  // ── Priority 2: Same PRIMARY category, different tenant ───────────────────
  if (currentPrimaryCategory) {
    fill(
      allActiveOffers.filter(
        (o) =>
          !usedIds.has(o.sys.id) &&
          o.tenant?.sys?.id !== tenantId &&
          getPrimaryCategory(o) === currentPrimaryCategory,
      ),
    );
  }
  if (result.length >= MAX_RELATED_OFFERS) return result;

  // ── Priority 3: Same SECONDARY category, different tenant ─────────────────
  if (currentSecondaryCategories.size > 0) {
    fill(
      allActiveOffers.filter(
        (o) =>
          !usedIds.has(o.sys.id) &&
          o.tenant?.sys?.id !== tenantId &&
          getSecondaryCategories(o).some((c) =>
            currentSecondaryCategories.has(c),
          ),
      ),
    );
  }
  if (result.length >= MAX_RELATED_OFFERS) return result;

  // ── Priority 4: Livli offers ──────────────────────────────────────────────
  fill(
    allActiveOffers.filter(
      (o) =>
        !usedIds.has(o.sys.id) &&
        o?.typeOfOffer?.internalName?.toLowerCase()?.includes('specialoffer'),
    ),
  );
  if (result.length >= MAX_RELATED_OFFERS) return result;

  // ── Priority 5: Random fallback ───────────────────────────────────────────
  fill(
    allActiveOffers
      .filter((o) => !usedIds.has(o.sys.id))
      .sort(() => Math.random() - 0.5),
  );

  return result;
}
